import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { components } from "@qorakul/shared/api";
import { api } from "./api.ts";

export type LessonAttendance = components["schemas"]["LessonAttendance"];
export type StudentAttendance = components["schemas"]["StudentAttendance"];
export type AttendanceStatus = NonNullable<components["schemas"]["AttendanceStatus"]>;
export type ClassLessonAttendance = components["schemas"]["ClassLessonAttendance"];

export const markStatuses: AttendanceStatus[] = ["Present", "Late", "Absent"];
export const attendanceStatusColors: Record<AttendanceStatus, string> = { Present: "green", Late: "gold", Absent: "red", Excused: "blue" };

export const attendanceKeys = {
  all: (classId: string) => ["attendance", classId] as const,
  week: (classId: string, from: string) => ["attendance", classId, "week", from] as const,
  lesson: (classId: string, lessonId: string) => ["attendance", classId, "lesson", lessonId] as const,
};

/** Attendance counts of the class's lessons from `from` to `to` (GET /attendance/classes/{classId}), keyed by lesson id. */
export function useClassAttendance(classId: string, from: string, to: string, enabled: boolean) {
  const { t } = useTranslation();
  return useQuery({
    queryKey: attendanceKeys.week(classId, from),
    queryFn: async () => {
      const { data, error } = await api.GET("/attendance/classes/{classId}", { params: { path: { classId }, query: { From: from, To: to } } });
      if (error || !data) throw new Error(t("app.error"));
      return new Map(data.map((lesson) => [lesson.lessonId, lesson]));
    },
    enabled,
  });
}
